import { baseService } from "src/store/base.store";
import { Lounge } from "./types/lounge.entity";
import { LoungeFilterInput, LoungeFilterQuery, LoungeWithUsers } from "./types";
import { executeQuery } from "src/store";
import { loungeRepository } from "./lounge.repository";
import { userRepository } from "../users/repositories/user.repository";
import { buildLoungeQuery } from "./utils/buildLoungeQuery";
import { User } from "../users/types/user.entity";

const loungeService = baseService.injectEndpoints({
  endpoints: (builder) => ({
    getLoungeRequests: builder.query<Lounge[], { where: LoungeFilterInput; offset: string }>({
      queryFn: ({ where, offset }) =>
        executeQuery(async () => {
          const filter = await buildLoungeQuery(where);

          return loungeRepository.find({ where: filter, offset, orderBy: ["createdAt", "desc"] });
        }),
    }),
    countLoungeRequests: builder.query<number, { where: LoungeFilterInput }>({
      queryFn: ({ where }) =>
        executeQuery(async () => {
          const filter = await buildLoungeQuery(where);

          return loungeRepository.count(filter);
        }),
    }),
    getLoungeRequest: builder.query<LoungeWithUsers, string>({
      queryFn: (id) =>
        executeQuery(async () => {
          const lounge = await loungeRepository.findById(id);
          if (!lounge) return null;

          const roleForParticipants: "COACH" | "LEARNER" = lounge.initiatorType === "COACH" ? "LEARNER" : "COACH";

          const selectedSet = new Set(lounge.selectedParticipantIds ?? []);

          const toParticipant = (u: User) => ({
            id: u.id,
            name: u.name,
            email: u.email,
            pictureURL: u.pictureURL,
            phoneNumber: u.phoneNumber,
            role: roleForParticipants,
            isSelected: selectedSet.has(u.id),
          });

          const [initiator, joiners] = await Promise.all([
            userRepository.findById(lounge.userId),
            lounge.joiners?.length ? userRepository.findByIds(lounge.joiners) : Promise.resolve([]),
          ]);

          const joinersMap = Object.fromEntries(
            joiners.map((j) => [j.id, toParticipant(j)])
          ) as LoungeWithUsers["joiners"];

          return {
            lounge,
            initiator: initiator ? { ...toParticipant(initiator), role: lounge.initiatorType } : null,
            joiners: joinersMap,
          };
        }),
    }),
  }),
});

export const { useGetLoungeRequestsQuery, useCountLoungeRequestsQuery, useGetLoungeRequestQuery } = loungeService;
